import React, { useMemo, useContext } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { AuthContext, AuthContextType } from 'lib/auth/AuthContext';
import { Layout } from 'components/Layout';
import { MarketplaceAdResponse, Typeform, Vehicle } from 'types/MarketplaceAdResponse';
import { Reviews } from 'components/sections/Reviews';
import { reviews } from 'content/reviews';
import { VehicleType } from 'enums/VehicleType';
import { Icon } from 'components/tokens/Icon';
import { alertTriangle, arrowRight, checkCircle, legalScale, leaf } from 'icons/default';
import { Colors } from 'enums/Colors';
import { Equipment } from 'components/views/ProductItem/components/Equipment';
import useMediaQuery from 'utils/useMediaQuery';
import { ImagesGallery } from 'components/ImagesGallery';
import { Badge } from 'components/Badge';
import { Button, ButtonSize, ButtonType, ButtonVariant } from 'components/Button';
import { HowItWorks } from 'components/sections/HowItWorks';
import { MarketplaceAdsResponse } from 'types/MarketplaceAdsResponse';
import { Magazine, MagazineArticleCard } from 'components/sections/Magazine';
import { Proof } from 'components/sections/Proof';
import { ProofProps } from 'components/sections/Proof/Proof';
import { Gutter } from 'components/Gutter';
import { getFormattedPrice } from 'utils/getFormattedPrice';
import { Modal } from 'components/Modal';
import { IReadMoreItem } from 'components/views/ProductItem/components/Description/Description';
import { getTypeFormTitle, getTypeFormValues } from 'utils/getTypeformData';
import { getCarConditionItem } from 'utils/getCondition';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { getTranslations } from 'utils/getTranslations';
import NordeaFinance from 'public/assets/logotype/nordeafinance.svg';

import { MoreImages } from './components/MoreImages';
import { ConditionItems } from './components/ConditionItems';
import { CarBadges } from './components/CarBadges';
import { Control } from './components/Control';
import { Description } from './components/Description';
import { Highlights } from './components/Highlights';
import { SoldBy } from './components/SoldBy';
import { LicensePlate } from './components/LicensePlate';
import { PartnerBox } from './components/PartnerBox';
// import { StatusBoxes } from './components/StatusBoxes';
import { CarInfo } from './components/CarInfo';
import { Header } from './components/Header';
import { Buy } from './components/Buy';
import styles from './ProductItem.module.scss';
import { getSavingsValue } from './actions';

interface Props {
  apiResponse: MarketplaceAdResponse;
  marketplaceAds: MarketplaceAdsResponse | undefined;
  articles: MagazineArticleCard[];
  proof: ProofProps;
  isLoading: boolean;
  error: any;
}

const getCondtionItems = (typeform: Typeform[], vehicleType: Vehicle['vehicle_type']) => {
  // TODO add other vehicleTypes
  switch (vehicleType.toLocaleLowerCase()) {
    case VehicleType.Car:
      return getCarConditionItem(typeform);
    case VehicleType.Truck:
      return getCarConditionItem(typeform);
    default:
      return [];
  }
};

export const ProductItem: React.FC<Props> = ({
  apiResponse,
  marketplaceAds,
  articles,
  proof,
  error,
  isLoading,
}) => {
  const { isLoggedIn } = useContext(AuthContext) as AuthContextType;
  const isMobileAndTablet = useMediaQuery('(max-width: 992px)');
  const validIcon = <Icon icon={checkCircle} color={Colors.MintSweet500} />;
  const warningIcon = <Icon icon={alertTriangle} color={Colors.Warning} />;
  const router = useRouter();
  const url = router.asPath;

  const ImagesSlider = dynamic(import('../../ImagesSlider'), {
    ssr: false,
    loading: () => null, // TODO add loader if jumpy loadtime on mobile
  });

  const { vehicle, seller_name: sellerName, typeform } = apiResponse.payload;

  const sellerInfo = {
    sellerId: apiResponse.payload.seller_id,
    sellerName,
    sellerEmail: apiResponse.payload.seller_email,
    sellerPhone: apiResponse.payload.seller_phone,
  };

  const customCarTitle = apiResponse.payload.custom_car_title;

  const badges = useMemo(() => getTypeFormValues(typeform, 'equipment'), [typeform]);
  const conditionItems = useMemo(
    () => getCondtionItems(typeform, vehicle.vehicle_type),
    [typeform, vehicle.vehicle_type]
  );

  const mileage = useMemo(() => getTypeFormValues(typeform, 'mileage'), [typeform]);

  const highlightsHeaderItems = useMemo(
    () => ({
      monthlyCost: vehicle.monthly_cost.monthly_cost,
      carInfoValuation: getFormattedPrice(apiResponse.payload.price),
      price: apiResponse.payload.price,
    }),
    [vehicle.monthly_cost, apiResponse.payload.price]
  );

  const highlightsItems = useMemo(
    () => ({
      vehicleMalus: vehicle.vehicle_malus,
      vehicleTax: vehicle.vehicle_tax,
      numberOfOwners: vehicle.number_of_owners,
      combined: vehicle.combined,
      modelYear: vehicle.model_year,
      miles: mileage,
      typeOfFuel: vehicle.type_of_fuel,
      range: vehicle.range,
    }),
    [
      vehicle.vehicle_malus,
      vehicle.vehicle_tax,
      vehicle.number_of_owners,
      vehicle.combined,
      vehicle.model_year,
      vehicle.type_of_fuel,
      vehicle.range,
      mileage,
    ]
  );

  const headerImagesHigh = apiResponse.payload.images
    .filter((image) => image.children.length > 1)
    .map((image) => image.children[1].full_path);

  const headerImages = apiResponse.payload.images
    .filter((image) => image.children.length > 1)
    .map((image) => image.children[0].full_path);

  const headerBadges: any[] = [];

  const savings = getSavingsValue(apiResponse.payload);
  const savingsString = savings ? `${savings > 0 ? '+' : ''}${getFormattedPrice(savings)}` : '0';

  if (vehicle.environmentally_friendly && apiResponse.payload.tag !== 'SOLD') {
    headerBadges.push({
      id: 'environmentally_friendly',
      badge: (
        <Modal
          ButtonContent={
            <Badge color={Colors.Yellowish} size="small" icon={leaf} questionmarkIcon>
              Bra miljöval
            </Badge>
          }
        >
          <h4>Bra miljöval</h4>
          <p>
            Bilar som släpper ut mindre än 70 gram CO<sub>2</sub>/km. Alternativt drivs med hjälp av
            el, kan tankas med gas eller är en laddhybrid.
          </p>
        </Modal>
      ),
    });
  }
  if (savings !== undefined && savingsString.includes('−')) {
    headerBadges.push({
      id: 'market-value-badge',
      badge: (
        <Modal
          ButtonContent={
            <Badge color={Colors.MintSweet} icon={legalScale} size="small" questionmarkIcon>
              {savingsString}
            </Badge>
          }
        >
          <h4>Jämförelsepris</h4>
          <p>
            Här visar vi en jämförelse mellan pris på blipp och vad motsvarande fordon kostar hos en
            bilhandlare.
          </p>
          <p>
            Värderingen görs av car.info och är baserad på ett estimerat försäljningspris som
            fordonet skulle haft hos en bilhandlare den dag det lades ut på blipp.se.
          </p>
        </Modal>
      ),
    });
  }

  if (apiResponse.payload.car_reserved && apiResponse.payload.tag !== 'SOLD') {
    headerBadges.push({
      id: 'reserved-badge',
      badge: (
        <Modal
          ButtonContent={
            <Badge color={Colors.Redish} size="small" questionmarkIcon>
              Reserverad
            </Badge>
          }
        >
          <h4>Reserverad</h4>
          <p>
            Bilen är reserverad då säljaren har accepterat ett bud. Om det inte blir någon affär
            släpps reservationen igen.
          </p>
        </Modal>
      ),
    });
  }

  if (apiResponse.payload.tag === 'SOLD') {
    headerBadges.push({
      id: 'sold-badge',
      badge: (
        <Badge color={Colors.Yellowish500} size="small">
          Såld
        </Badge>
      ),
    });
  }

  const isofixValue = vehicle.isofix.values.map((position) =>
    position === 'Rear' ? 'Baksäte' : 'Framsäte'
  );

  const carInfoItems = [
    {
      label: 'Tidigare ägare',
      value: vehicle.number_of_owners,
    },
    {
      label: 'Plats',
      value: apiResponse.payload.municipality?.name,
    },
    {
      label: 'Importerad',
      value: vehicle.imported ? 'Ja' : 'Nej',
    },
    {
      label: 'Mätarställning',
      value: `${mileage} mil`,
    },
    vehicle.type_of_fuel === 'Elbil'
      ? {
          label: 'Räckvidd',
          value: vehicle.range,
        }
      : {
          label: 'Förbrukning',
          value: vehicle.combined,
        },
    {
      label: 'Bränsle',
      value: vehicle.type_of_fuel,
    },
    {
      label: 'Varumärke',
      value: vehicle.brand,
    },
    {
      label: 'Modell',
      value: vehicle.model,
    },
    {
      label: 'Klassificering',
      value: vehicle.classification,
    },
    {
      label: 'Färg',
      value: vehicle.colour,
    },
    {
      label: 'Antal sittplatser',
      value: vehicle.number_of_seats,
    },
    { label: 'Fordonsskatt', value: getFormattedPrice(vehicle.vehicle_tax) ?? '-' },
    { label: 'Skattemånad', value: vehicle.taxation_month ?? '-' },
    {
      label: 'Förhöjd fordonsskatt (år 1-3)',
      value: vehicle.vehicle_malus ? getFormattedPrice(vehicle.vehicle_malus) : '-',
    },
    {
      label: 'Hästkrafter',
      value: vehicle.horsepower ? `${vehicle.horsepower} HK` : '-',
    },
    { label: 'Blandad förbrukning', value: vehicle.combined ?? '-' },
    { label: 'Växellåda', value: vehicle.gearbox ?? '-' },
    { label: 'Drift', value: vehicle.drive_wheels_type ?? '-' },
    { label: 'Dragkrok', value: vehicle.tow_bar ? 'Ja' : 'Nej' },
    {
      label: 'Dragvikt obromsat släp',
      value: vehicle.trailer_weight_unbraked ? `${vehicle.trailer_weight_unbraked} kg` : '-',
    },
    {
      label: 'Dragvikt bromsat släp',
      value: vehicle.trailer_weight_with_brakes ? `${vehicle.trailer_weight_with_brakes} kg` : '-',
    },
    {
      label: 'Lastvikt',
      value: vehicle.load_weight ? `${vehicle.load_weight} kg` : '-',
    },
    {
      label: 'Totalvikt',
      value: vehicle.gross_weight ? `${vehicle.gross_weight} kg` : '-',
    },
    { label: 'Isofix', value: isofixValue.join(', ') },
  ];

  if (!apiResponse.payload.municipality?.name) {
    carInfoItems.splice(1, 1);
  }

  const controlItems = [
    { label: 'Senaste besiktning', value: vehicle.latest_inspection },
    { label: 'Nästa besiktning', value: vehicle.inspection_valid_to },
    {
      label: 'Ej rapporterat körförbud',
      value: vehicle.prohibition_driving ? warningIcon : validIcon,
    },
    {
      label: 'Ej rapporterad stulen',
      value: vehicle.theft ? warningIcon : validIcon,
    },
    {
      label: 'Ej rapporterad i olycka',
      value: vehicle.accident ? warningIcon : validIcon,
    },
    {
      label: 'Ej rapporterad skrotad',
      value: vehicle.scrap ? warningIcon : validIcon,
    },
    {
      label: 'Kontrollerad mätarställning',
      value: vehicle.mileage_warning ? warningIcon : validIcon,
    },
    { label: 'I trafik', value: vehicle.in_traffic ? validIcon : warningIcon },
  ];

  const description = getTypeFormValues(typeform, 'highlights')[0];

  const descriptionReadMoreItems = useMemo(() => {
    const hasUnrepairedDamage = getTypeFormValues(typeform, 'unrepaired_faults')[0] !== '0';
    return [
      {
        title: getTypeFormTitle(typeform, 'repairs_carried_out_details'),
        text: getTypeFormValues(typeform, 'repairs_carried_out_details')[0],
      },
      hasUnrepairedDamage
        ? {
            title: getTypeFormTitle(typeform, 'unrepaired_faults'),
            text: getTypeFormValues(typeform, 'error_details')[0],
          }
        : null,
      {
        title: getTypeFormTitle(typeform, 'service_history'),
        text: getTypeFormValues(typeform, 'service_history')[0],
      },
      {
        title: getTypeFormTitle(typeform, 'missing_in_service_history'),
        text: getTypeFormValues(typeform, 'missing_in_service_history')[0],
      },
      {
        title: getTypeFormTitle(typeform, 'last_service'),
        text: getTypeFormValues(typeform, 'last_service')[0],
      },
      {
        title: getTypeFormTitle(typeform, 'warranty_included'),
        text: getTypeFormValues(typeform, 'warranty_included')[0] === '1' ? 'Ja' : 'Nej',
      },
      {
        title: getTypeFormTitle(typeform, 'timing_belt'),
        text: getTypeFormValues(typeform, 'timing_belt')[0],
      },
    ].filter((item) => item?.text) as Array<IReadMoreItem>; // never going to be null, typescript don't know that before compiling
  }, [typeform]);

  const equipmentText = getTypeFormValues(typeform, 'accessories').map((accessory) => ({
    label: accessory,
  }));

  const breadcrumbsItems = useMemo(() => {
    let vehicleType;
    let vehicleTypeLowerCase;
    if (vehicle.vehicle_filter_type) {
      vehicleType = vehicle.vehicle_filter_type;
      vehicleTypeLowerCase = vehicle.vehicle_filter_type.toLowerCase();
    } else {
      vehicleType = 'Vehicle';
      vehicleTypeLowerCase = '';
    }

    return [
      { text: 'Köpa', href: '/fordon' },
      {
        text: getTranslations(vehicleType),
        href: `/fordon/?fordonstyp=${vehicleTypeLowerCase}`,
      },
      {
        text: vehicle.brand,
        href: `/fordon/?fordonstyp=${vehicleTypeLowerCase}&marke=${vehicle.brand}`,
      },
      {
        text: vehicle.series,
        href: `/fordon/?fordonstyp=${vehicleTypeLowerCase}&marke=${vehicle.brand}&modell=${vehicle.series}`,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle.brand, vehicle.model, vehicle.vehicle_filter_type]);

  const hasDataResult = !error && !isLoading && marketplaceAds?.payload?.items.length;

  return (
    <Layout>
      <div className={styles.wrapper}>
        <div className={styles.images}>
          {isMobileAndTablet ? (
            <ImagesSlider images={headerImages} badges={headerBadges} />
          ) : (
            <ImagesGallery
              images={headerImages}
              imagesHigh={headerImagesHigh}
              badges={headerBadges}
            />
          )}
        </div>
        <div className={styles.container}>
          <div>
            <Gutter size="small" />
            <div className={styles['breadcrumbs-wrapper']}>
              <Breadcrumbs staticBreadcrumbs={breadcrumbsItems} />
            </div>
            {isMobileAndTablet && (
              <>
                <Header
                  title={`${vehicle.brand} ${vehicle.model_gen_engine ?? vehicle.model}`}
                  customCarTitle={customCarTitle}
                  subtitle={`${
                    vehicle.trim_package ? `${vehicle.trim_package.replace(',', ' •')} •` : ''
                  } ${vehicle.drive_wheels_type ? `${vehicle.drive_wheels_type} •` : ''} ${
                    vehicle.horsepower ? `${vehicle.horsepower} hk` : ''
                  }`}
                />
                {vehicle.is_company_leasable && (
                  <h4 className={styles.companyLeasable}>Möjlighet till att företagsleasa</h4>
                )}
              </>
            )}
            {!isMobileAndTablet && (
              <>
                <Header
                  title={`${vehicle.brand} ${vehicle.model_gen_engine ?? vehicle.model}`}
                  subtitle={`${
                    vehicle.trim_package ? `${vehicle.trim_package.replace(',', ' •')} •` : ''
                  } ${vehicle.drive_wheels_type ? `${vehicle.drive_wheels_type} •` : ''} ${
                    vehicle.horsepower ? `${vehicle.horsepower} hk` : ''
                  }`}
                  year={vehicle.model_year}
                  miles={mileage}
                  engineType={vehicle.type_of_fuel}
                  gearbox={vehicle.gearbox}
                  customCarTitle={customCarTitle}
                />
                {vehicle.is_company_leasable && (
                  <h4 className={styles.companyLeasable}>Möjlighet till att företagsleasa</h4>
                )}
              </>
            )}
            <Gutter size="small" />
            {vehicle.vehicle_type === 'MC' ? (
              <p className={styles.badgeParagraph}>{badges}</p>
            ) : (
              <CarBadges badges={badges} paginated />
            )}
            {apiResponse.payload.tag !== 'SOLD' ? (
              <>
                <Gutter size="small" />
                <div className={`${styles.bid} ${styles.mobile}`}>
                  <Button
                    type={ButtonType.Button}
                    size={ButtonSize.Large}
                    trailingIcon={apiResponse.payload.car_reserved ? null : arrowRight}
                    onClick={() => {
                      router.push(`/app/bid/make?ad=${apiResponse.payload.entity_id}`);
                    }}
                    variant={
                      apiResponse.payload.car_reserved
                        ? ButtonVariant.Primary
                        : ButtonVariant.PrimarySkyBlue
                    }
                    disabled={apiResponse.payload.car_reserved}
                  >
                    {apiResponse.payload.car_reserved ? 'Fordonet är reserverat' : 'Skicka bud'}
                  </Button>
                  <Gutter size="tiny" />

                  {apiResponse.payload.tag !== 'SOLD' &&
                  !apiResponse.payload.car_reserved &&
                  sellerInfo?.sellerId != null &&
                  !isLoggedIn ? (
                    <div>
                      <dl className={styles['dl-wrapper']}>
                        <div>
                          <dt className={styles.dt}>Visas för inloggade</dt>
                          <dd className={styles.license_plate}>
                            <span>Skicka meddelande till säljaren</span>
                          </dd>
                        </div>
                        <Gutter size="tiny" />
                        <Button
                          type={ButtonType.Button}
                          size={ButtonSize.Large}
                          trailingIcon={arrowRight}
                          href={`/app/forward?page=${url}`}
                          variant={ButtonVariant.PrimaryMintSweet}
                          nofollow
                        >
                          Skicka meddelande
                        </Button>
                      </dl>
                    </div>
                  ) : null}
                  <Gutter size="tiny" />
                </div>
              </>
            ) : null}
            <Gutter size="small" />
            <Highlights headerItems={highlightsHeaderItems} items={highlightsItems} />
            <Gutter size="small" />
            <div className={styles.mobile}>
              {apiResponse.payload.tag !== 'SOLD' ? (
                <SoldBy
                  sellerName={sellerName}
                  municipality={apiResponse.payload.municipality?.name ?? ''}
                />
              ) : null}
              <Gutter size="small" />
              <LicensePlate licensePlate={vehicle.license_plate} isLoggedIn={isLoggedIn} />
              <Gutter size="small" />
              {apiResponse.payload.tag !== 'SOLD' && !apiResponse.payload.car_reserved ? (
                <>
                  <Buy
                    totalCost={apiResponse.payload.price}
                    monthlyCost={vehicle.monthly_cost.monthly_cost}
                    licensePlate={vehicle.license_plate}
                    leasable={vehicle.is_company_leasable}
                  />
                  <Gutter size="large" />
                </>
              ) : null}
            </div>
            <Gutter size="medium" />

            {description || descriptionReadMoreItems.length ? (
              <>
                <Gutter size="large" />
                <div className={styles['border-wrapper']}>
                  <Description text={description} readMoreItems={descriptionReadMoreItems} />
                </div>
                <Gutter size="medium" />
              </>
            ) : (
              <Gutter size="medium" />
            )}
            {conditionItems.length ? (
              <div className={styles['border-wrapper']}>
                <ConditionItems items={conditionItems} />
              </div>
            ) : null}
            <Gutter size="medium" />
            <Equipment
              badges={badges}
              listItems={equipmentText}
              vehicleType={vehicle.vehicle_type}
            />
            <Gutter size="medium" />
            <CarInfo carInfoItems={carInfoItems} />
            <Gutter size="large" />
            <div className={styles.mobile}>
              <Control items={controlItems} />
              <PartnerBox
                title="Serviceavtal från Mekonomen"
                description="Med Mekonomen serviceavtal slipper du obehagliga överraskningar, teckna ett redan idag. Gäller över hela landet!"
                href="/app/forward?partner=mekonomen"
                imageSrc="/assets/images/mekonomen-logo.png"
                buttonText="Läs mer"
                dataLayerEvent="mekonomen"
              />
              {/* 
              <Gutter size="small" />
              {vehicle.is_electric && (
                <PartnerBox
                  title="Laddbox från EFUEL med Blipp-rabatt"
                  description="Boka rådgivning eller beställ direkt från EFUEL. Få 500kr rabatt genom Blipp!"
                  href="/app/forward?partner=efuel"
                  imageSrc="/assets/images/efuel-laddbox-min.jpeg"
                  buttonText="Läs mer"
                  dataLayerEvent="efuel"
                />
              )}
              */}
              <Gutter size="large" />
            </div>
          </div>
          <div className={`${styles.desktop}`}>
            <div className={styles.bid}>
              <Gutter size="small" />
              {headerImages.length ? (
                <>
                  <MoreImages images={headerImages} />
                  <Gutter size="tiny" />
                </>
              ) : null}
              {apiResponse.payload.tag !== 'SOLD' ? (
                <Button
                  type={ButtonType.Button}
                  size={ButtonSize.Large}
                  trailingIcon={apiResponse.payload.car_reserved ? null : arrowRight}
                  onClick={() => {
                    router.push(`/app/bid/make?ad=${apiResponse.payload.entity_id}`);
                  }}
                  variant={
                    apiResponse.payload.car_reserved
                      ? ButtonVariant.Primary
                      : ButtonVariant.PrimarySkyBlue
                  }
                  disabled={apiResponse.payload.car_reserved}
                >
                  {apiResponse.payload.car_reserved ? 'Fordonet är reserverat' : 'Skicka bud'}
                </Button>
              ) : null}
              <Gutter size="tiny" />

              <Gutter size="tiny" />
            </div>
            <Gutter size="small" />
            {apiResponse.payload.tag !== 'SOLD' && !apiResponse.payload.car_reserved ? (
              <Buy
                totalCost={apiResponse.payload.price}
                monthlyCost={vehicle.monthly_cost.monthly_cost}
                licensePlate={vehicle.license_plate}
                leasable={vehicle.is_company_leasable}
              />
            ) : null}
            <Gutter size="small" />
            {apiResponse.payload.tag !== 'SOLD' ? (
              <>
                <SoldBy
                  sellerName={sellerName}
                  municipality={apiResponse.payload.municipality?.name ?? ''}
                />
                <Gutter size="small" />
                <LicensePlate licensePlate={vehicle.license_plate} isLoggedIn={isLoggedIn} />
                <Gutter size="small" />
              </>
            ) : null}
            <Control items={controlItems} />
            <PartnerBox
              title="Serviceavtal från Mekonomen"
              description="Med Mekonomen serviceavtal slipper du obehagliga överraskningar, teckna ett redan idag. Gäller över hela landet!"
              href="/app/forward?partner=mekonomen"
              imageSrc="/assets/images/mekonomen-logo.png"
              buttonText="Läs mer"
              dataLayerEvent="mekonomen"
            />
            {/* 
            <Gutter size="small" />
            {vehicle.is_electric && (
              <PartnerBox
                title="Laddbox från EFUEL med Blipp-rabatt"
                description="Boka rådgivning eller beställ direkt från EFUEL. Få 500kr rabatt genom Blipp!"
                href="/app/forward?partner=efuel"
                imageSrc="/assets/images/efuel-laddbox-min.jpeg"
                buttonText="Läs mer"
                dataLayerEvent="efuel"
              />
            )}
            */}
          </div>
        </div>
        <Gutter size="small" />
        <HowItWorks
          heading="Ett lån med bilen som säkerhet."
          imageSrc="/assets/images/woman-reading-in-trunk.jpg"
          color={Colors.DarkBlue}
          alt="Kvinna läser bok i bakluckan på en bil."
        >
          <p>
            Låna med bilen som säkerhet precis som hos bilhandlaren. Samma ränta för alla när du
            köper via Blipp.
          </p>
          <NordeaFinance />
          <div className={styles['how-it-works-button']}>
            <Button
              type={ButtonType.Button}
              href="/lanelofte-billan"
              size={ButtonSize.Large}
              trailingIcon={arrowRight}
              variant={ButtonVariant.GhostInverted}
            >
              Läs mer om billånet
            </Button>
          </div>
        </HowItWorks>
        <Gutter size="large" />
        {hasDataResult ? (
          marketplaceAds.payload &&
          marketplaceAds.payload.items && (
            <Proof
              subTitle="Liknande fordon"
              cars={marketplaceAds.payload.items}
              blurbs={proof.blurbs}
              urls={proof.urls}
              buttons={false}
            />
          )
        ) : (
          <div className={`${styles['loading-wrapper']} ${styles.box}`}>
            <div className={styles.box}>
              <video autoPlay loop muted playsInline>
                <source src="/assets/videos/blipp-logotyp-azur-animation.mp4" type="video/mp4" />
              </video>

              <p>Laddar...</p>
            </div>
          </div>
        )}
        <Gutter size="xlarge" />
        <Magazine articles={articles} />
        <Gutter size="large" />
        <Reviews title={reviews.title} reviews={reviews.reviews} />
        <Gutter size="negative" />
      </div>
    </Layout>
  );
};
